import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/date';
import { openNotificationLink } from '@/utils/notificationLink';
import { NotificationIcon } from './NotificationIcon';
import type { Notification } from '@/types/notification';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onClick?: () => void;
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onClick,
}: NotificationItemProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!notification.is_read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    if (notification.action_url) {
      openNotificationLink(notification.action_url, navigate);
    }

    onClick?.();
  };

  const timeAgo = formatRelativeTime(notification.created_at, { addSuffix: false });

  return (
    <button
      onClick={handleClick}
      className={cn(
        'w-full flex items-start gap-3 p-3 text-left transition-colors',
        'hover:shadow-vess hover:-translate-y-px',
        !notification.is_read && 'bg-muted/30'
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        <NotificationIcon
          type={notification.type}
          priority={notification.priority}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p
            className={cn(
              'text-sm font-medium truncate',
              !notification.is_read && 'text-foreground',
              notification.is_read && 'text-muted-foreground'
            )}
          >
            {notification.title}
          </p>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {timeAgo}
          </span>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
          {notification.message}
        </p>
      </div>
      {!notification.is_read && (
        <div className="flex-shrink-0 mt-1">
          <div className="h-2 w-2 rounded-full bg-primary" />
        </div>
      )}
    </button>
  );
}
